import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ScoreGauge from "@/components/results/ScoreGauge";
import Timeline from "@/components/results/Timeline";
import { api } from "@/lib/api";
import { fmt$, fmtPct } from "@/lib/format";
import { ArrowRight } from "lucide-react";

export default function SharedReport() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/shared/${token}`).then(r => setDoc(r.data)).catch(() => setErr("This shared report was not found."));
  }, [token]);

  if (err) return (
    <>
      <Nav />
      <main className="container-hs py-24 text-center">
        <h1 className="font-display text-3xl mb-3">Report not found</h1>
        <p className="text-brand-muted mb-6">{err}</p>
        <Link to="/" className="btn-primary">Back to Home Cost Path</Link>
      </main>
      <Footer />
    </>
  );
  if (!doc) return <div className="min-h-screen flex items-center justify-center text-brand-muted">Loading…</div>;

  const r = doc.results || {};
  const inputs = doc.inputs || {};
  const isVA = doc.financing_type === "va";

  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <span className="badge-soft" data-testid="shared-badge">Read-only shared report</span>
            <h1 className="font-display text-3xl sm:text-4xl mt-2">{doc.nickname}</h1>
            <p className="text-sm text-brand-muted mt-1">Shared from Home Cost Path — see the real cost before you buy.</p>
          </div>
          <Link to="/analyze" className="btn-primary text-sm py-2 px-4">Run your own <ArrowRight size={14}/></Link>
        </div>

        <div data-testid="card-cash-max" className="card-hs p-8 sm:p-10 mb-6 bg-brand-soft/60 border-brand-accent/40">
          <div className="text-xs uppercase tracking-wider text-brand-muted">Estimated Maximum Cash Needed During This Purchase</div>
          <div className="num-xl mt-3 text-brand-accent">{fmt$(r.max_transaction_cash)}</div>
          <p className="text-sm text-brand-muted mt-3 max-w-2xl">The most cash you might need to have access to at any single moment while buying this home.{r.money_temporarily_tied_up > 0 ? " This includes earnest money that is temporarily out of your account even though it is expected to be applied as a credit toward amounts due at closing." : ""}</p>
        </div>

        {r.reserve_shortfall > 0 && (
          <div className="mb-6 rounded-2xl border border-brand-warning/50 bg-brand-warning/10 p-5 sm:p-6">
            <div className="font-display text-lg text-brand-ink">Cash cushion is below the desired reserve</div>
            <p className="text-sm text-brand-muted mt-1">After this purchase, Home Cost Path estimates about <b className="text-brand-ink">{fmt$(Math.max(r.cash_remaining || 0, 0))}</b> would remain — roughly <b className="text-brand-ink">{fmt$(r.reserve_shortfall)}</b> below the preferred reserve of <b className="text-brand-ink">{fmt$(r.desired_reserve)}</b>.</p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {[
            { k: "Cash Needed Before Closing", v: fmt$(r.cash_before_closing), sub: "Earnest, inspections, appraisal" },
            { k: "Additional Cash at Closing", v: fmt$(r.additional_cash_at_closing), sub: "After credits" },
            { k: "Estimated Monthly Housing", v: fmt$(r.monthly_total), sub: "PITI + HOA + other" },
            { k: "Estimated Cash Cushion", v: r.cash_remaining != null ? fmt$(r.cash_remaining) : "—", sub: "After purchase" },
          ].map(c => (
            <div key={c.k} className="card-hs p-6">
              <div className="text-xs uppercase tracking-wider text-brand-muted">{c.k}</div>
              <div className="num-lg mt-3">{c.v}</div>
              <div className="text-xs text-brand-muted mt-2">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="card-hs p-6 lg:col-span-1">
            {r.score_available ? (
              <>
                <ScoreGauge score={r.score || 0} />
                <div className="mt-4 space-y-2">
                  {r.score_reasons?.strengths?.map((s) => <div key={`s:${s}`} className="text-xs text-brand-success flex gap-2"><span>•</span>{s}</div>)}
                  {r.score_reasons?.watch?.map((s) => <div key={`w:${s}`} className="text-xs text-brand-warning flex gap-2"><span>•</span>{s}</div>)}
                </div>
              </>
            ) : (
              <div className="py-4">
                <div className="text-xs uppercase tracking-wider text-brand-muted">Home Cost Path Purchase Score</div>
                <div className="font-display text-2xl mt-2 text-brand-muted">Not enough information yet</div>
                <p className="text-sm text-brand-muted mt-3">More details are needed to generate a Home Cost Path Purchase Score.</p>
              </div>
            )}
          </div>
          <div className="card-hs p-6 lg:col-span-2">
            <h2 className="font-display text-2xl mb-4">Here's the Important Part</h2>
            <div className="space-y-3 text-brand-ink leading-relaxed">
              <p>Before closing day, approximately <b>{fmt$(r.cash_before_closing)}</b> may be needed for earnest money and pre-closing expenses.</p>
              {r.money_temporarily_tied_up > 0 && <p>Of that, about <b>{fmt$(r.money_temporarily_tied_up)}</b> is temporarily out of the account (earnest money expected to be applied as a credit toward amounts due at closing), while <b>{fmt$(r.money_likely_spent)}</b> is money that will likely be permanently spent (inspections, appraisal, and similar fees).</p>}
              <p>An additional <b>{fmt$(r.additional_cash_at_closing)}</b> may be needed on closing day.</p>
              <p>The most cash needed at any single point during the transaction: <b className="text-brand-accent">{fmt$(r.max_transaction_cash)}</b>.</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="card-hs p-6">
            <h3 className="font-display text-xl mb-4">True Monthly Housing Cost</h3>
            <Row k="Principal & Interest" v={fmt$(r.principal_interest)} />
            <Row k="Property Taxes" v={fmt$(r.taxes_monthly)} />
            <Row k="Insurance" v={fmt$(r.insurance_monthly)} />
            <Row k="HOA" v={fmt$(r.hoa_monthly)} />
            <Row k="Other Recurring" v={fmt$(r.other_housing_monthly)} />
            <div className="border-t border-brand-border mt-3 pt-3 flex justify-between font-semibold">
              <span>Estimated True Monthly</span><span className="text-brand-accent">{fmt$(r.monthly_total)}</span>
            </div>
          </div>
          {isVA && (
            <div className="card-hs p-6">
              <h3 className="font-display text-xl mb-4">VA Loan Summary</h3>
              <Row k="Purchase Price" v={fmt$(r.purchase_price)} />
              <Row k="Down Payment" v={`${fmt$(r.down_payment_amount)} (${fmtPct(r.down_payment_pct)})`} />
              <Row k="Base Loan" v={fmt$(r.base_loan)} />
              <Row k="VA Funding Fee" v={fmt$(r.funding_fee)} />
              <Row k="Total Financed" v={fmt$(r.total_financed)} />
              <Row k="Rate / Term" v={`${inputs.interest_rate || 0}% / ${inputs.term_years}yr`} />
              <Row k="Est. P&I" v={fmt$(r.principal_interest)} />
            </div>
          )}
        </div>

        <div className="card-hs p-6 mb-8">
          <h3 className="font-display text-xl mb-6">Home-Buying Cash Timeline</h3>
          <Timeline preItems={inputs.pre_closing_items || []} additionalCashAtClosing={r.additional_cash_at_closing || 0} />
        </div>

        <div className="text-xs text-brand-muted rounded-2xl border border-brand-border bg-brand-soft/40 p-5">
          This is a read-only planning snapshot generated by Home Cost Path. Estimates are for planning
          only and are not loan offers, approvals, appraisals, or guarantees. Home Cost Path is not
          affiliated with or endorsed by the U.S. Department of Veterans Affairs.
        </div>
      </main>
      <Footer />
    </>
  );
}
function Row({ k, v }) { return <div className="flex justify-between py-2 text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-ink">{v}</span></div>; }
